import { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import ScrollToTopButton from '@components/common/ScrollToTopButton'
import { PageHero, Reveal, Icon, ICONS, NAVY, GOLD_ICON, GOLD } from '@components/marketing'
import { fieldClass, FieldLabel } from '@components/auth/AuthFields'

const CONTACT_EMAIL = 'info@kudhi.net'

const COUNTRY_CODES = ['+225', '+226', '+227', '+228', '+229', '+221', '+223', '+224']

const SUBJECTS = ['Question générale', 'Mon compte', 'Publication d\'une annonce', 'Paiement', 'Partenariat / entreprise', 'Autre']

const INFOS = [
  { icon: 'pin', label: 'Siège', value: 'Grand Bassam, rue des 20233' },
  { icon: 'call', label: 'Téléphone', value: '+225 05 06 76 77 17', href: 'tel:+2250506767717' },
  { icon: 'mail', label: 'Email', value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
  { icon: 'doc', label: 'Adresse postale', value: '06 BP 2243 Abidjan 06' },
  { icon: 'globe', label: 'Site web', value: 'www.kudhi.net', href: 'https://www.kudhi.net/' },
]

function ContactPage() {
  const [formData, setFormData] = useState({
    civilite: '',
    prenom: '',
    nom: '',
    email: '',
    indicatif: '+225',
    telephone: '',
    sujet: SUBJECTS[0],
    message: '',
  })

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  // Aucun service d'envoi n'est branché côté API : le message est préparé
  // dans la messagerie de l'utilisateur, adressé à l'équipe KUDHI.
  const sendEmail = (e) => {
    e.preventDefault()
    const fullName = [formData.civilite, formData.prenom, formData.nom].filter(Boolean).join(' ')
    const body = [
      formData.message,
      '',
      '—',
      `Nom : ${fullName}`,
      `Email : ${formData.email}`,
      formData.telephone ? `Téléphone : ${formData.indicatif} ${formData.telephone}` : null,
    ].filter((l) => l !== null).join('\n')

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`[Contact KUDHI] ${formData.sujet}`)}&body=${encodeURIComponent(body)}`
    toast.success('Votre messagerie s\'ouvre avec le message pré-rempli.')
  }

  return (
    <div className="home-font bg-white text-slate-900 overflow-x-hidden">
      <PageHero
        badge="Réponse sous 24 à 48 h"
        badgeIcon="chat"
        title="Parlons de"
        highlight="votre projet."
        subtitle="Une question, un partenariat ou besoin d'aide ? Écrivez-nous, notre équipe vous répond rapidement."
      />

      <section className="pb-24 pt-4">
        <div className="w-[90%] md:w-[80%] mx-auto grid lg:grid-cols-[1fr_1.6fr] gap-8 items-start">
          {/* Formulaire */}
          <Reveal className="rounded-md border border-slate-200/70 bg-white p-6 sm:p-10 shadow-sm">
            <h2 className="text-2xl font-extrabold tracking-tight" style={{ color: NAVY }}>Envoyez-nous un message</h2>
            <p className="mt-1 text-slate-500">Les champs marqués d&apos;un * sont obligatoires.</p>

            <form className="mt-8 space-y-5" onSubmit={sendEmail}>
              <div>
                <FieldLabel>Civilité</FieldLabel>
                <div className="flex flex-wrap gap-2">
                  {['M', 'Mme', 'Mlle', 'Autre'].map((c) => {
                    const active = formData.civilite === c
                    return (
                      <label
                        key={c}
                        className={`cursor-pointer rounded-md border px-5 py-2.5 text-sm font-semibold transition-all ${
                          active ? 'border-transparent shadow-sm' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                        style={active ? { background: NAVY, color: '#fff' } : {}}
                      >
                        <input type="radio" name="civilite" value={c} checked={active} onChange={handleChange} className="sr-only" />
                        {c}
                      </label>
                    )
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <FieldLabel htmlFor="prenom">Prénom *</FieldLabel>
                  <input id="prenom" type="text" name="prenom" value={formData.prenom} onChange={handleChange} required autoComplete="given-name" placeholder="Votre prénom" className={`${fieldClass} px-4`} />
                </div>
                <div>
                  <FieldLabel htmlFor="nom">Nom *</FieldLabel>
                  <input id="nom" type="text" name="nom" value={formData.nom} onChange={handleChange} required autoComplete="family-name" placeholder="Votre nom" className={`${fieldClass} px-4`} />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <FieldLabel htmlFor="email">Email *</FieldLabel>
                  <input id="email" type="email" name="email" value={formData.email} onChange={handleChange} required autoComplete="email" placeholder="vous@exemple.com" className={`${fieldClass} px-4`} />
                </div>
                <div>
                  <FieldLabel htmlFor="telephone">Téléphone</FieldLabel>
                  <div className="flex gap-2">
                    <select name="indicatif" value={formData.indicatif} onChange={handleChange} aria-label="Indicatif pays" className={`${fieldClass} !w-28 flex-shrink-0 px-3 cursor-pointer`}>
                      {COUNTRY_CODES.map((code) => <option key={code} value={code}>{code}</option>)}
                    </select>
                    <input id="telephone" type="tel" name="telephone" value={formData.telephone} onChange={handleChange} autoComplete="tel-national" placeholder="Numéro" className={`${fieldClass} flex-1 min-w-0 px-4`} />
                  </div>
                </div>
              </div>

              <div>
                <FieldLabel htmlFor="sujet">Sujet</FieldLabel>
                <select id="sujet" name="sujet" value={formData.sujet} onChange={handleChange} className={`${fieldClass} px-4 cursor-pointer`}>
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <FieldLabel htmlFor="message">Message *</FieldLabel>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={6} placeholder="Décrivez votre demande…" className={`${fieldClass} px-4 resize-none`} />
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-slate-500 max-w-xs">
                  En envoyant ce message, vous acceptez notre{' '}
                  <Link to="/privacy" className="font-semibold underline underline-offset-2" style={{ color: NAVY }}>politique de confidentialité</Link>.
                </p>
                <button
                  type="submit"
                  className="group inline-flex items-center justify-center gap-2 rounded-md px-8 py-4 font-bold shadow-lg shadow-amber-300/40 transition-all hover:-translate-y-0.5 hover:shadow-xl"
                  style={{ background: GOLD, color: NAVY }}
                >
                  Envoyer le message
                  <Icon d={ICONS.arrow} className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.2} />
                </button>
              </div>
            </form>
          </Reveal>

          {/* Coordonnées */}
          <Reveal delay={120} className="lg:order-first lg:sticky lg:top-28 space-y-5">
            <div className="relative overflow-hidden rounded-md p-8 shadow-[0_20px_50px_-20px_rgba(245,183,0,0.6)]" style={{ background: 'linear-gradient(135deg, #FFE45C 0%, #FFD700 45%, #F5B700 100%)' }}>
              <div className="absolute inset-0 home-grid-bg-light pointer-events-none" />
              <div className="absolute -bottom-24 -right-16 w-64 h-64 rounded-full bg-white/30 blur-3xl pointer-events-none" />
              <div className="relative">
                <p className="text-sm font-bold uppercase tracking-widest" style={{ color: 'rgba(0,42,78,0.65)' }}>Nos coordonnées</p>
                <h3 className="mt-2 text-2xl font-extrabold" style={{ color: NAVY }}>KUDHI</h3>
                <ul className="mt-6 space-y-5">
                  {INFOS.map((info) => {
                    const value = info.href ? (
                      <a href={info.href} target={info.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="font-semibold text-[#002A4E] hover:underline underline-offset-2">{info.value}</a>
                    ) : (
                      <span className="font-semibold text-[#002A4E]">{info.value}</span>
                    )
                    return (
                      <li key={info.label} className="flex items-start gap-4">
                        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md" style={{ background: 'rgba(255,255,255,0.7)', color: NAVY }}>
                          <Icon d={ICONS[info.icon]} className="w-5 h-5" />
                        </span>
                        <span className="leading-tight">
                          <span className="block text-xs text-[#002A4E]/60">{info.label}</span>
                          {value}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>

            <Link to="/help" className="group flex items-center gap-4 rounded-md border border-slate-200/70 bg-white p-6 transition-all hover:shadow-lg hover:border-transparent">
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md" style={GOLD_ICON}>
                <Icon d={ICONS.question} />
              </span>
              <span className="flex-1">
                <span className="block font-bold" style={{ color: NAVY }}>Centre d&apos;aide</span>
                <span className="block text-sm text-slate-500">La réponse est peut-être déjà dans la FAQ.</span>
              </span>
              <span className="text-slate-400 transition-transform group-hover:translate-x-1">
                <Icon d={ICONS.arrow} className="w-5 h-5" strokeWidth={2} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <ScrollToTopButton />
    </div>
  )
}

export default ContactPage
